// /**
//  * Analytics Constants
//  * Central configuration for all analytics settings
//  */

// export const ANALYTICS_CONFIG = {
//   site: {
//     brand: 'Resmed',
//     region: 'APAC',
//     country: 'IN',
//     language: 'en',
//     environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
//     siteType: 'support',
//     userLoginState: false,
//     employee: false,
//     provider: typeof window !== 'undefined' ? window.location.hostname : ''
//   },
  
//   debug: process.env.NODE_ENV !== 'production',
  
//   page_type_map: {
//     'home': 'support_home',
//     'index': 'support_home',
//     '/': 'support_home',
//     'category': 'support_category',
//     '/support': 'support_article',
//     'search': 'support_search',
//     'download': 'support_download',
//     'not-found': 'support_not_found',
//     'default': 'support_page'
//   }
// };

// export const DEVICE_TYPES = {
//   MOBILE: 'mobile',
//   TABLET: 'tablet',
//   DESKTOP: 'desktop'
// };

// export const EVENT_NAMES = {
//   SUPPORT_SEARCH: 'support_search',
//   CATEGORY_CLICK: 'category_click',
//   PRODUCT_CLICK: 'product_click',
//   FILE_DOWNLOAD: 'file_download',
//   MENU_CLICK: 'menu_click',
//   CTA_CLICK: 'cta_click',
//   HYPERLINK_CLICK: 'hyperlink_click',
//   PRODUCT_TAB_CLICK: 'product_tab_click',
//   FILTER_CLICK: 'filter_click',
//   SLIDER_CLICK: 'slider_click'
// };

// export const EVENT_PRIORITIES = {
//   file_download: 1,
//   menu_click: 2,
//   cta_click: 3,
//   hyperlink_click: 4
// };

// export const PAGEVIEW_EVENT = 'pageview';

/**
 * Region Mapping
 * Define regions before using them in locale pattern generation
 */
export const REGION_MAP = {
  'en-au': {
    language: 'en',
    country: 'AU',
    region: 'APAC'
  },

  'de-de': {
    language: 'de',
    country: 'DE',
    region: 'EMEA'
  },

  'fr-fr': {
    language: 'fr',
    country: 'FR',
    region: 'EMEA'
  },

  'fr-ch': {
    language: 'fr',
    country: 'CH',
    region: 'EMEA'
  },

  'de-ch': {
    language: 'de',
    country: 'CH',
    region: 'EMEA'
  },

  'it-it': {
    language: 'it',
    country: 'IT',
    region: 'EMEA'
  },

  'es-sp': {
    language: 'es',
    country: 'ES',
    region: 'EMEA'
  },

  'ja-jp': {
    language: 'ja',
    country: 'JP',
    region: 'APAC'
  },

  'zh-cn': {
    language: 'zh',
    country: 'CN',
    region: 'APAC'
  },

  'sv-se': {
    language: 'sv',
    country: 'SE',
    region: 'EMEA'
  },

  'nb-no': {
    language: 'nb',
    country: 'NO',
    region: 'EMEA'
  },

  'fi-fi': {
    language: 'fi',
    country: 'FI',
    region: 'EMEA'
  },

  'da-dk': {
    language: 'da',
    country: 'DK',
    region: 'EMEA'
  },

  'pt-br': {
    language: 'pt',
    country: 'BR',
    region: 'AMER'
  },

  'es-xl': {
    language: 'es',
    country: 'XL',
    region: 'AMER'
  }
};

/**
 * Generate dynamic locale pattern from REGION_MAP
 * Automatically includes all supported regions
 */
const getLocalePattern = () => {
  // Define known regions first (primary locales)
  const knownRegions = [
    'en-au', 'en-gb', 'en-us', 'en-ca', 'en-nz',
    'de-de', 'de-ch',
    'fr-fr', 'fr-ch',
    'it-it',
    'es-sp', 'es-xl',
    'ja-jp',
    'zh-cn',
    'sv-se',
    'nb-no',
    'fi-fi',
    'da-dk',
    'pt-br'
  ];
  
  // Combine with any additional regions from REGION_MAP
  const allRegions = new Set([
    ...knownRegions,
    ...Object.keys(REGION_MAP)
  ]);
  
  return Array.from(allRegions).join('|');
};

const LOCALE_PATTERN = getLocalePattern();

export const ANALYTICS_CONFIG = {
  site: {
    brand: 'Resmed',
    region: 'APAC',
    country: 'IN',
    language: 'en',
    environment:
      process.env.NODE_ENV === 'production'
        ? 'production'
        : 'development',
    siteType: 'support',
    userLoginState: false,
    employee: false,
    provider:
      typeof window !== 'undefined'
        ? window.location.hostname
        : ''
  },

  debug: process.env.NODE_ENV !== 'production',

  /**
   * Route-based page mapping
   * Used by router.afterEach()
   * Locale pattern is dynamically generated from REGION_MAP
   */
  route_page_type_map: [
    /**
     * Root homepage
     * /en-au or /en-au/
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/?$`),
      type: 'support_home',
      template: 'homepage',
      event: 'homepage_view'
    },

    /**
     * Astral FSN pages
     */
    {
      match: /^\/astralfsn$/,
      type: 'support_article',
      template: 'astral_fsn',
      event: 'astral_fsn_page_click'
    },

    {
      match: /^\/page-technique-astral-fsn-2023-phase-2$/,
      type: 'support_article',
      template: 'astral_fsn',
      event: 'astral_fsn_page_click'
    },

    /**
     * Smart sheet form
     */
    {
      match: /^\/en-au\/smart-sheet-form$/,
      type: 'support_article',
      template: 'smart_sheet_form',
      event: 'smart_sheet_form_click'
    },

    /**
     * Search page
     * /en-au/search or /en-au/search/
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/search/?$`),
      type: 'support_search',
      template: 'search_results',
      event: 'support_search_click'
    },

    /**
     * Non listed page
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/non-listed/?$`),
      type: 'support_article',
      template: 'non_listed',
      event: 'non_listed_page_click'
    },

    /**
     * Support homepage
     * /en-au/support or /en-au/support/
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/support/?$`),
      type: 'support_home',
      template: 'support_home',
      event: 'article_click'
    },

    /**
     * Support article
     * /en-au/support/article-name
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/support/.+/?$`),
      type: 'support_article',
      template: 'support_article',
      event: 'support_article_click'
    },

    /**
     * Category + slug
     * /en-au/category/product
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/[^/]+/[^/]+/?$`),
      type: 'support_product_name',
      template: 'product_name',
      event: 'product_click'
    },
    /**
     * Category + slug
     * /en-au/category or /en-au/category/
     */
     {
      match: new RegExp(`^/(${LOCALE_PATTERN})/[^/]+/?$`),
      type: 'support_category',
      template: 'category',
      event: 'category_click'
    },

    /**
     * Generic content pages
     * /en-au/support/slug
     */
    {
      match: new RegExp(`^/(${LOCALE_PATTERN})/support/[^/]+/?$`),
      type: 'support_article',
      template: 'content_page',
      event: 'content_page_click'
    },

    /**
     * 404 pages
     */
    {
      match: /404/,
      type: 'support_not_found',
      template: 'not_found'
    }
  ]
};

export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

export const EVENT_NAMES = {
  SUPPORT_SEARCH: 'support_search',
  CATEGORY_CLICK: 'category_click',
  PRODUCT_CLICK: 'product_click',
  FILE_DOWNLOAD: 'file_download',
  MENU_CLICK: 'menu_click',
  CTA_CLICK: 'cta_click',
  HYPERLINK_CLICK: 'hyperlink_click',
  PRODUCT_TAB_CLICK: 'product_tab_click',
  FILTER_CLICK: 'filter_click',
  SLIDER_CLICK: 'slider_click'
};

export const EVENT_PRIORITIES = {
  file_download: 1,
  menu_click: 2,
  cta_click: 3,
  hyperlink_click: 4
};

export const PAGEVIEW_EVENT = 'pageview';
